import {
  Control,
  Controller,
  FieldValues,
  RegisterOptions,
} from 'react-hook-form';

import {
  getFieldErrorValue,
  getValidations,
} from '@/components/utils/inputUtils';
import { FieldNameFromFormShape } from '@/types/react-hook-form';

import { ButtonGroup } from '../baseInputs/ButtonGroup';
import {
  ButtonGroupInputOption,
  ButtonGroupVariant,
  HelpTextVariant,
} from '../baseInputs/inputTypes';
import { useFormFieldsDisabled } from '../context/formFieldsDisabled.context';

export interface FormAwareButtonGroupProps<FormShape extends FieldValues> {
  fieldName: FieldNameFromFormShape<FormShape>;
  label: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  options: ButtonGroupInputOption[];
  required?: boolean;
  variant?: ButtonGroupVariant;
  control: Control<FormShape>;
  contextualHelp?: JSX.Element;
  validation?: RegisterOptions<FormShape>['validate'];
  disabled?: boolean;
  hideLabel?: boolean;
  /** Use the style for input buttons instead of tabs.  Defaults to true. */
  isInput?: boolean;
}

export function FormAwareButtonGroup<FormShape extends FieldValues>({
  fieldName,
  label,
  helpText,
  helpTextVariant,
  options,
  required,
  variant,
  control,
  contextualHelp,
  disabled,
  validation,
  hideLabel,
  isInput = true,
}: FormAwareButtonGroupProps<FormShape>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const validations = getValidations(label, !!required, validation);
  return (
    <Controller
      name={fieldName}
      control={control}
      rules={{ validate: validations }}
      render={({ field, fieldState, formState }) => {
        return (
          <ButtonGroup
            inputRef={field.ref}
            isInput={isInput}
            label={label}
            variant={variant}
            options={options}
            onChange={field.onChange}
            contextualHelp={contextualHelp}
            value={field.value}
            name={field.name}
            required={required}
            helpText={helpText}
            helpTextVariant={helpTextVariant}
            errorMessage={getFieldErrorValue(fieldState, formState.isSubmitted)}
            disabled={disabled || disabledFromContext}
            hideLabel={hideLabel}
          />
        );
      }}
    />
  );
}
